import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { FileSignature, ShieldCheck, ScrollText, CheckCircle2, Clock, PenLine, Eye, X, Fingerprint, Upload, AlertTriangle, FileDown } from "lucide-react";
import api, { formatApiErrorDetail, downloadAuthedFile } from "@/lib/api";
import SalesAcademy from "@/components/sales/SalesAcademy";
import { AcademyCertificateButton } from "@/components/sales/AcademyCertificate";
import EmailVerifyCard from "@/components/EmailVerifyCard";

const EASE = [0.16, 1, 0.3, 1];
const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";

const DOC_META = {
  nda: { label: "Non-Disclosure Agreement", icon: ShieldCheck, desc: "Protects studio projects, builds and business information." },
  ip: { label: "IP Assignment Agreement", icon: ScrollText, desc: "Assigns all work you create to the Company — required for copyright protection." },
  contract: { label: "Software Development Agreement", icon: FileSignature, desc: "Your engagement terms — scope, rate and payment." },
};

function IdentityStep({ status, onSubmitted }) {
  const [legalName, setLegalName] = useState(status.legal_name || "");
  const [idType, setIdType] = useState("passport");
  const [file, setFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (status.legal_name) setLegalName(status.legal_name);
  }, [status.legal_name]);

  const submit = async () => {
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("legal_name", legalName);
      fd.append("id_type", idType);
      fd.append("consent", "true");
      fd.append("file", file);
      await api.post("/onboarding/identity", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Identity submitted — the studio will review it");
      onSubmitted();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const st = status.id_status;
  const staleVerified = st === "verified" && !status.has_document;
  const badge = st === "verified" && !staleVerified
    ? { text: "Verified", cls: "text-nc-green border-nc-green/40 bg-nc-green/10", icon: CheckCircle2 }
    : staleVerified
      ? { text: "Resubmission required", cls: "text-nc-orange border-nc-orange/40 bg-nc-orange/10", icon: AlertTriangle }
      : st === "pending"
      ? { text: "Under review", cls: "text-nc-orange border-nc-orange/40 bg-nc-orange/10", icon: Clock }
      : st === "rejected"
        ? { text: "Rejected — resubmit", cls: "text-red-400 border-red-500/40 bg-red-500/10", icon: AlertTriangle }
        : null;

  return (
    <motion.section data-testid="onboarding-identity" className="border border-white/10 bg-black/40 p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Fingerprint className="w-5 h-5 text-nc-orange" />
          <div>
            <h3 className="font-sans font-bold uppercase text-base text-zinc-50">1. Identity Verification</h3>
            <p className="font-mono text-[10px] text-zinc-500 mt-0.5">Government-issued ID — proves the legal name on your contracts is really you.</p>
          </div>
        </div>
        {badge && (
          <span data-testid="identity-badge" className={`font-mono text-[9px] tracking-[0.25em] uppercase border px-3 py-1.5 flex items-center gap-1.5 ${badge.cls}`}>
            <badge.icon className="w-3.5 h-3.5" /> {badge.text}
          </span>
        )}
      </div>

      {st === "verified" && !staleVerified ? (
        <p className="font-mono text-[11px] text-zinc-400 mt-3 leading-relaxed">
          Verified as <span className="text-zinc-100">{status.legal_name}</span>. Your signatures and invoices are legally linked to this identity.
        </p>
      ) : (
        <div className="flex flex-col gap-4 mt-4">
          {staleVerified && (
            <div data-testid="identity-stale-warning" className="border border-nc-orange/40 bg-nc-orange/5 px-4 py-3 font-mono text-[11px] text-nc-orange">
              Your previous verification has no ID document on file. Contract signing is paused until you resubmit a clear copy of your government-issued ID below.
            </div>
          )}
          {st === "rejected" && (
            <div data-testid="identity-reject-reason" className="border border-red-500/30 bg-red-500/5 px-4 py-3 font-mono text-[11px] text-red-300">
              Rejected: {status.id_reject_reason}
            </div>
          )}
          {/* Privacy collection notice — Privacy Act 1988 (Cth) */}
          <div className="border border-nc-blue/30 bg-nc-blue/5 p-4">
            <p className="font-mono text-[9px] tracking-[0.25em] text-nc-blue uppercase mb-2">Privacy Collection Notice — Privacy Act 1988 (Cth)</p>
            <p className="font-mono text-[10px] text-zinc-400 leading-relaxed">
              Specpixel Entertainment PTY LTD collects your legal name and ID document solely to verify your identity for contractor
              agreements and payments, as required for lawful engagement of overseas and domestic workers. Your document is stored in
              encrypted cloud storage, accessible only to studio administration, and is never shared with third parties. You may request
              access, correction or deletion at any time via the support desk, per the Australian Privacy Principles.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5">Full legal name (as on your ID) *</label>
              <input data-testid="identity-name-input" className={inputCls} value={legalName} onChange={(e) => setLegalName(e.target.value)} placeholder="Exactly as printed on your ID" />
            </div>
            <div>
              <label className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5">ID document type *</label>
              <select data-testid="identity-type-select" className={inputCls} value={idType} onChange={(e) => setIdType(e.target.value)}>
                <option value="passport">Passport</option>
                <option value="drivers-licence">Driver's Licence</option>
                <option value="national-id">National ID Card</option>
                <option value="proof-of-age-card">Proof of Age Card</option>
              </select>
            </div>
          </div>
          <div>
            <label className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5">Upload ID document * (jpg/png/webp/pdf, max 10MB)</label>
            <button data-testid="identity-upload-button" onClick={() => fileRef.current?.click()} className="border border-dashed border-white/20 hover:border-nc-blue px-4 py-5 w-full font-mono text-xs text-zinc-400 hover:text-nc-blue transition-colors flex items-center justify-center gap-2">
              <Upload className="w-4 h-4" /> {file ? file.name : "Choose file…"}
            </button>
            <input ref={fileRef} data-testid="identity-file-input" type="file" accept="image/png,image/jpeg,image/webp,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input data-testid="identity-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 accent-[#2E6BFF]" />
            <span className="font-mono text-[10px] text-zinc-400 leading-relaxed">
              I consent to the collection and storage of my identity document as described above, and declare the name provided is my true legal name.
            </span>
          </label>
          <button data-testid="identity-submit" onClick={submit} disabled={sending || !consent || !file || legalName.trim().length < 2} className="self-start bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-5 py-2.5 flex items-center gap-2 transition-colors">
            <Fingerprint className="w-3.5 h-3.5" /> {sending ? "Uploading…" : st === "rejected" ? "Resubmit Identity" : "Submit for Verification"}
          </button>
        </div>
      )}
    </motion.section>
  );
}

function SignGate({ docType }) {
  return (
    <div data-testid={`sign-locked-${docType}`} className="border border-nc-orange/30 bg-nc-orange/5 px-4 py-3 mt-3 flex items-start gap-3">
      <Fingerprint className="w-4 h-4 text-nc-orange shrink-0 mt-0.5" />
      <p className="font-mono text-[10px] text-zinc-400 leading-relaxed">
        <span className="text-nc-orange uppercase tracking-[0.15em]">Locked.</span> Legal documents can only be signed after your identity is verified with an uploaded ID document — complete step 1 above and wait for studio review.
      </p>
    </div>
  );
}

function SignFlow({ docType, contractId, templateText, onSigned }) {
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [signing, setSigning] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const sign = async () => {
    setSigning(true);
    try {
      await api.post("/contracts/sign", { doc_type: docType, signed_name: name, consent, contract_id: contractId || "" });
      toast.success("Signed and recorded");
      onSigned();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSigning(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 mt-3">
      <button data-testid={`sign-read-${docType}`} onClick={() => setExpanded((e) => !e)} className="self-start font-mono text-[10px] tracking-[0.2em] uppercase text-nc-blue hover:text-zinc-100 flex items-center gap-2 transition-colors">
        <Eye className="w-3.5 h-3.5" /> {expanded ? "Hide" : "Read"} full document
      </button>
      {expanded && (
        <pre data-testid={`sign-text-${docType}`} className="border border-white/10 bg-[#050505]/70 p-4 font-mono text-[11px] text-zinc-300 leading-relaxed whitespace-pre-wrap max-h-72 overflow-y-auto">
          {templateText}
        </pre>
      )}
      <div>
        <label className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5">Type your full legal name to sign</label>
        <input data-testid={`sign-name-${docType}`} className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="Full legal name" />
      </div>
      <label className="flex items-start gap-3 cursor-pointer">
        <input data-testid={`sign-consent-${docType}`} type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 accent-[#2E6BFF]" />
        <span className="font-mono text-[10px] text-zinc-400 leading-relaxed">
          I consent to sign electronically under the Electronic Transactions Act 1999 (Cth) and agree this typed name is my legally binding signature.
        </span>
      </label>
      <button data-testid={`sign-submit-${docType}`} onClick={sign} disabled={signing || !consent || name.trim().length < 2} className="self-start bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-5 py-2.5 flex items-center gap-2 transition-colors">
        <PenLine className="w-3.5 h-3.5" /> {signing ? "Recording…" : "Sign Document"}
      </button>
    </div>
  );
}

export default function OnboardingTab({ user, onNavigateInvoices }) {
  const [data, setData] = useState({ signed: [], pending: [] });
  const [templates, setTemplates] = useState({});
  const [identity, setIdentity] = useState({ id_status: "not-submitted", legal_name: "" });
  const [viewDoc, setViewDoc] = useState(null);
  const [viewText, setViewText] = useState("");
  const [academy, setAcademy] = useState(null);
  const [academyBusy, setAcademyBusy] = useState(false);

  const loadAcademy = useCallback(() => {
    api.get("/dev/onboarding").then((r) => setAcademy(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadAcademy(); }, [loadAcademy]);

  const completeAcademyStep = async (stepId) => {
    setAcademyBusy(true);
    try {
      const r = await api.post(`/dev/onboarding/${stepId}/complete`);
      toast.success(r.data.done ? "Contractor Academy complete — marketplace and gallery unlocked" : "Step completed");
      loadAcademy();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAcademyBusy(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const r = await api.get("/contracts/mine");
      setData(r.data);
      const [nda, ip, idStatus] = await Promise.all([api.get("/contracts/template/nda"), api.get("/contracts/template/ip"), api.get("/onboarding/identity/status")]);
      setTemplates({ nda: nda.data.rendered_text, ip: ip.data.rendered_text });
      setIdentity(idStatus.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const signedTypes = new Set(data.signed.map((d) => d.doc_type));
  const idVerified = identity.id_status === "verified" && identity.has_document !== false;
  const ndaSigned = signedTypes.has("nda");
  const ipSigned = signedTypes.has("ip");
  const pendingContract = data.pending[0];
  const hasSignedContract = data.signed.some((d) => d.doc_type === "contract");
  const contractDone = hasSignedContract || !pendingContract;
  const stepsDone = [idVerified, ndaSigned, ipSigned, contractDone].filter(Boolean).length;
  const complete = idVerified && ndaSigned && ipSigned && contractDone;

  const openSigned = async (docId) => {
    const r = await api.get(`/contracts/${docId}/text`);
    setViewText(r.data.rendered_text);
    setViewDoc({ ...r.data, id: docId });
  };

  const downloadPdf = async (d) => {
    try {
      await downloadAuthedFile(`/contracts/${d.id}/pdf`, `SP-${d.doc_type.toUpperCase()}-${String(d.signed_at).slice(0, 4)}-${d.id.slice(0, 6).toUpperCase()}.pdf`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div data-testid="onboarding-tab" className="flex flex-col gap-6 max-w-4xl">
      {/* Contractor Academy — training program */}
      {academy && !academy.done && (
        <SalesAcademy
          onboarding={academy}
          onComplete={completeAcademyStep}
          busy={academyBusy}
          testid="dev-academy"
          title="Contractor Academy — Training Program"
          subtitle={academy.required ? "Required before you can list services or marketplace work" : "Recommended — master how engagements, delivery and payouts work"}
        />
      )}
      {academy?.done && (
        <div data-testid="dev-academy-done" className="border border-nc-green/40 bg-nc-green/5 px-5 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-nc-green shrink-0" />
            <p className="font-mono text-[11px] text-zinc-300"><span className="text-nc-green font-bold uppercase tracking-[0.2em] text-[10px]">Contractor Academy complete.</span> You know the rules, the standards and how you get paid — the marketplace and gallery are yours.</p>
          </div>
          <AcademyCertificateButton path="/dev/onboarding/certificate/pdf" filename="specpixel-contractor-academy-certificate.pdf" testid="dev-certificate-download" />
        </div>
      )}

      {/* Progress */}
      <div className="border border-white/10 bg-black/40 p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="font-mono text-[9px] tracking-[0.3em] text-nc-orange uppercase">Onboarding Checklist</p>
            <p data-testid="onboarding-progress" className="font-sans font-bold text-xl text-zinc-50 mt-1">{stepsDone} / 4 complete</p>
          </div>
          {complete ? (
            <span data-testid="onboarding-complete" className="font-mono text-[10px] tracking-[0.25em] uppercase text-nc-green border border-nc-green/40 bg-nc-green/10 px-3 py-2 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Fully onboarded — you can submit invoices
            </span>
          ) : (
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-nc-orange border border-nc-orange/40 bg-nc-orange/10 px-3 py-2">
              Invoices unlock after ID verification + NDA + IP
            </span>
          )}
        </div>
        <div className="flex gap-1 mt-4">
          {[idVerified, ndaSigned, ipSigned, contractDone].map((done, i) => (
            <span key={i} className={`flex-1 h-1 ${done ? "bg-nc-green" : "bg-white/10"}`} />
          ))}
        </div>
      </div>

      {/* Identity */}
      <IdentityStep status={identity} onSubmitted={load} />

      {/* NDA */}
      <motion.section data-testid="onboarding-nda" className="border border-white/10 bg-black/40 p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-nc-blue" />
            <div>
              <h3 className="font-sans font-bold uppercase text-base text-zinc-50">2. Non-Disclosure Agreement</h3>
              <p className="font-mono text-[10px] text-zinc-500 mt-0.5">{DOC_META.nda.desc}</p>
            </div>
          </div>
          {ndaSigned && <span data-testid="nda-signed-badge" className="font-mono text-[9px] tracking-[0.25em] uppercase text-nc-green border border-nc-green/40 bg-nc-green/10 px-3 py-1.5 flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Signed</span>}
        </div>
        {!ndaSigned && (idVerified ? <SignFlow docType="nda" contractId="" templateText={templates.nda || "Loading…"} onSigned={load} /> : <SignGate docType="nda" />)}
      </motion.section>

      {/* IP */}
      <motion.section data-testid="onboarding-ip" className="border border-white/10 bg-black/40 p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.05, ease: EASE }}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <ScrollText className="w-5 h-5 text-nc-blue" />
            <div>
              <h3 className="font-sans font-bold uppercase text-base text-zinc-50">3. Intellectual Property Assignment</h3>
              <p className="font-mono text-[10px] text-zinc-500 mt-0.5">{DOC_META.ip.desc}</p>
            </div>
          </div>
          {ipSigned && <span data-testid="ip-signed-badge" className="font-mono text-[9px] tracking-[0.25em] uppercase text-nc-green border border-nc-green/40 bg-nc-green/10 px-3 py-1.5 flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Signed</span>}
        </div>
        {!ipSigned && (idVerified ? <SignFlow docType="ip" contractId="" templateText={templates.ip || "Loading…"} onSigned={load} /> : <SignGate docType="ip" />)}
      </motion.section>

      {/* Contract */}
      <motion.section data-testid="onboarding-contract" className="border border-white/10 bg-black/40 p-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1, ease: EASE }}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <FileSignature className="w-5 h-5 text-nc-blue" />
            <div>
              <h3 className="font-sans font-bold uppercase text-base text-zinc-50">4. Software Development Agreement</h3>
              <p className="font-mono text-[10px] text-zinc-500 mt-0.5">{DOC_META.contract.desc}</p>
            </div>
          </div>
          {contractDone ? (
            <span data-testid="contract-signed-badge" className="font-mono text-[9px] tracking-[0.25em] uppercase text-nc-green border border-nc-green/40 bg-nc-green/10 px-3 py-1.5 flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Signed</span>
          ) : !pendingContract ? (
            <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-zinc-500 border border-white/15 px-3 py-1.5 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Awaiting issue from admin</span>
          ) : null}
        </div>
        {pendingContract && (
          <div className="mt-3">
            <div className="border border-nc-orange/30 bg-nc-orange/5 p-4 mb-3">
              <p className="font-mono text-[10px] text-nc-orange uppercase tracking-[0.2em]">Contract issued — {pendingContract.rate_model} @ {pendingContract.rate || "agreed rate"}</p>
              <p className="font-mono text-[10px] text-zinc-400 mt-1">Scope: {pendingContract.scope}</p>
            </div>
            {idVerified ? (
              <SignFlow docType="contract" contractId={pendingContract.id} templateText="The full Software Development Services Agreement with your engagement terms will be rendered with your signature details upon signing. Read the NDA and IP Agreement above for the full framework — this contract incorporates both." onSigned={load} />
            ) : (
              <SignGate docType="contract" />
            )}
          </div>
        )}
      </motion.section>

      {/* Signed documents */}
      {data.signed.length > 0 && (
        <section className="border border-white/10 bg-black/40 p-6">
          <p className="font-mono text-[9px] tracking-[0.3em] text-nc-blue uppercase mb-4">Your Signed Documents ({data.signed.length})</p>
          <div className="flex flex-col gap-2">
            {data.signed.map((d, i) => (
              <div key={d.id} data-testid={`signed-doc-${i + 1}`} className="flex items-center justify-between gap-4 border border-white/10 bg-[#050505]/60 px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <CheckCircle2 className="w-4 h-4 text-nc-green shrink-0" />
                  <div className="min-w-0">
                    <p className="font-sans font-semibold text-sm text-zinc-100">{DOC_META[d.doc_type]?.label || d.doc_type}</p>
                    <p className="font-mono text-[10px] text-zinc-600">Signed as {d.signed_name} // {String(d.signed_at).slice(0, 16).replace("T", " ")}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button data-testid={`signed-doc-pdf-${i + 1}`} onClick={() => downloadPdf(d)} className="font-mono text-[9px] tracking-[0.2em] uppercase text-nc-blue hover:text-zinc-100 transition-colors flex items-center gap-1.5"><FileDown className="w-3.5 h-3.5" /> PDF</button>
                  <button data-testid={`signed-doc-view-${i + 1}`} onClick={() => openSigned(d.id)} className="font-mono text-[9px] tracking-[0.2em] uppercase text-nc-blue hover:text-zinc-100 transition-colors">View</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* View modal */}
      {viewDoc && (
        <div className="fixed inset-0 z-[95] bg-black/85 flex items-center justify-center p-4" onClick={() => setViewDoc(null)}>
          <div data-testid="signed-doc-modal" className="border border-white/15 bg-[#0a0a0a] max-w-2xl w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-white/10 px-5 py-3 flex items-center justify-between">
              <span className="font-mono text-[10px] tracking-[0.3em] text-nc-green uppercase">{DOC_META[viewDoc.doc_type]?.label} — executed copy</span>
              <div className="flex items-center gap-4">
                <button data-testid="signed-doc-modal-pdf" onClick={() => downloadPdf(viewDoc)} className="font-mono text-[9px] tracking-[0.2em] uppercase text-nc-blue hover:text-zinc-100 transition-colors flex items-center gap-1.5"><FileDown className="w-3.5 h-3.5" /> Download PDF</button>
                <button data-testid="signed-doc-close" onClick={() => setViewDoc(null)} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <pre className="p-5 font-mono text-[11px] text-zinc-300 leading-relaxed whitespace-pre-wrap overflow-y-auto">{viewText}</pre>
          </div>
        </div>
      )}
    </div>
  );
}
